import React from "react";
import { useDispatch } from "react-redux";
import {
  highlightMistakes,
  clearHighlight,
  SectionType,
  MistakeType,
} from "lib/redux/highlightSlice";
import Image from "next/image";
import RepeatedSvg from "public/icons/score-check/RepeatedWords.svg";
import BuzzSvg from "public/icons/score-check/buzzWord.svg";
import SpellingSvg from "public/icons/score-check/SpeillingMistake.svg";

interface MistakesDisplayV2Props {
  spellingMistakes?: string[];
  buzzWords?: string[];
  repeatedWords?: string[];
  section: SectionType;
  itemIndex?: number | null;
}

export const MistakesDisplayV2: React.FC<MistakesDisplayV2Props> = ({
  spellingMistakes = [],
  buzzWords = [],
  repeatedWords = [],
  section,
  itemIndex = null,
}) => {
  const dispatch = useDispatch();

  const handleWordMouseEnter = (word: string, mistakeType: MistakeType) => {
    dispatch(
      highlightMistakes({
        section,
        itemIndex: itemIndex ?? null,
        mistakeType,
        texts: [word],
      })
    );
  };

  const handleWordMouseLeave = () => {
    dispatch(clearHighlight());
  };

  const renderColumn = (
    title: string,
    icon: React.ReactNode,
    mistakes: string[],
    mistakeType: MistakeType,
    emptyStateImage: string,
    emptyStateText: string
  ) => {
    const hasMistakes = mistakes.length > 0;

    // Render icon with responsive classes to match title line height.
    const renderIcon = () => {
      // container sizes to match title line-height across breakpoints
      const containerClasses = "w-6 h-6 lg:w-8 lg:h-8 xl:w-10 xl:h-10 2xl:w-12 2xl:h-12 flex items-center justify-start overflow-visible";
      if (React.isValidElement(icon)) {
        // ensure the svg fills the container while preserving aspect ratio
        return (
          <div className={containerClasses}>
            {React.cloneElement(icon as React.ReactElement, {
              style: { width: '100%', height: '100%', display: 'block' },
              // remove any conflicting className so container controls sizing
              className: undefined,
            })}
          </div>
        );
      }
      return <div className={containerClasses}>{icon}</div>;
    };

    return (
      // make the inner column stretch to full width so hover BG reaches the column borders
      <div className="flex min-w-0 w-full flex-1 flex-col p-1 items-stretch">
        <div className="flex flex-col items-start">
        <div className="mb-0 flex items-center justify-start">
          <div className="flex items-center justify-start">
            {renderIcon()}
          </div>
        </div>
        <h3 className="text-xs font-bold text-gray-800 lg:text-[10px] xl:text-[13px] 2xl:text-base mt-0.5">
          {title}
        </h3>
        </div>

        {hasMistakes ? (
          <div className="flex w-full flex-col mt-2 gap-2">
            {mistakes.map((mistake, idx) => (
              <div
                key={idx}
                className="cursor-pointer overflow-hidden whitespace-normal break-words break-all rounded-lg border-[0.5px] border-[#eeeeee] bg-[#FFFFFF] px-2 py-1 text-center text-xs [@media(min-width:1023px)]:text-[10px]  [@media(min-width:1256px)]:text-xs transition-colors hover:bg-[#FEE7E4] lg:py-1 lg:px-2 xl:px-2.5 xl:py-1.5 lg:text-base"
                onMouseEnter={() => handleWordMouseEnter(mistake, mistakeType)}
                onMouseLeave={handleWordMouseLeave}
              >
                {mistake}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="relative h-24 w-24 mt-2 lg:h-25 lg:w-25 xl:w-32 xl:h-32">
              <Image
                src={emptyStateImage}
                alt={emptyStateText}
                fill
                className="object-contain"
              />
            </div>
            <p className="text-center text-sm font-semibold text-blue-600 lg:text-xs xl:text-base">
              {emptyStateText}
            </p>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="lg:mt-3 xl:mt-1 relative rounded-2xl bg-[#FBF9FF] p-1 transition-colors lg:p-1.5 2xl:p-2">

      {/* Use explicit separator elements instead of divide-x so rounded corners don't bend the line */}
      <div className="mt-1 flex flex-col sm:flex-row gap-6 sm:gap-0 xl:gap-0 items-stretch">
        {/* First column - no left divider */}
        <div className="min-w-0 flex-1 rounded-lg lg:p-2 hover:bg-[#eae3fa]">
          {renderColumn(
            "Spelling mistakes",
            <SpellingSvg />,
            spellingMistakes,
            "spelling",
            "/assets/resumeScore/NoSpelling.png",
            "No Spelling Mistakes"
          )}
        </div>

        {/* explicit vertical separator using vector image */}
        <div className="hidden sm:flex items-center justify-center mx-0" aria-hidden="true">
          <Image src="/assets/resumeScore/sep.svg" alt="" width={2} height={8} className="h-full w-auto" />
        </div>

        {/* middle column */}
        <div className="min-w-0 flex-1 rounded-lg lg:p-2 hover:bg-[#eae3fa]">
          {renderColumn(
            "Common Buzz words",
            <BuzzSvg />,
            buzzWords,
            "buzzword",
            "/assets/resumeScore/NobuzzWords.png",
            "No Buzz-Words Detected"
          )}
        </div>

        {/* explicit vertical separator using vector image */}
        <div className="hidden sm:flex items-center justify-center mx-0" aria-hidden="true">
          <Image src="/assets/resumeScore/sep.svg" alt="" width={2} height={8} className="h-full w-auto" />
        </div>

        {/* right column */}
        <div className="min-w-0 flex-1 rounded-lg lg:p-2 hover:bg-[#eae3fa]">
          {renderColumn(
            "Repeated words",
            <RepeatedSvg />,
            repeatedWords,
            "actionVerb",
            "/assets/resumeScore/NoRepeat.png",
            "No Repeated Words Found"
          )}
        </div>
      </div>
    </div>
  );
};
